import { useEffect, useMemo, useState } from "react";
import { Settlement } from "../models/settlement/Settlement";
import { getAllSettlements, getSettlementById, getSettlementStatistics } from "../services/settlements";
import useUserAccount from "./userAccountHook";
import { capitalizeSentence, formatCurrencyAmount, getFormattedDate } from "../utils/miscFunctions";
import IconStatus from "../components/elements/IconStatus";
import { EllipsisVerticalIcon } from "lucide-react";
import PopOnMe from "../components/elements/poponme/PopOnMe";
import { useQueryClient } from "@tanstack/react-query";
import useModal from "./modalHook";
import CompleteSettlement from "../views/partners/shared/settlements/components/CompleteSettlement";
import { GTableActionColumn, ITablePaginationData } from "../components/elements/GTable/GTable";
import { Link, useSearchParams } from "react-router-dom";
import useRoute from "./routesHook";
import moment from "moment";


function useSettlements({ getTableRowActions }: ISettlementHookProps) {
    const { closeModal, updateModalStates, ModalComponent } = useModal();
    const [searchParams, setSearchParams] = useSearchParams();
     const currentPage = Number(searchParams.get("page")) || 0;
    const [loading, setLoading] = useState<boolean>(true);
    const [settlementsPaginationData, setSettlementsPaginationData] = useState<ITablePaginationData>({
        total: 0,
        per_page: 0,
        totalPages: 0,
        initialPage: currentPage ? currentPage : 0,
        to: 0,
        from: 0
    });
    const SettlementModal = ModalComponent;
    const { getDashboardBase } = useRoute();
    const { user } = useUserAccount()
    const queryClient = useQueryClient();

    const fetchAllsettlements = async (page?: number, size?: number, brokerId?: number, companyId?: number, fromDate?: string, toDate?: string, isPaid?: string, isApproved?: boolean, reviewStage?: string, reference?: string,) => {
        try {
            const response: any = await getAllSettlements(page, size, brokerId, companyId, fromDate, toDate, isPaid, isApproved, reviewStage, reference);

            if (response) {
                return response;
            }
            
        } catch (error) {
            console.log(error);
        } finally {
           
        }
    }

    const fetchSettlementById = async (id?: string) => {
        try {
            if (!id) return;
            const response: any = await getSettlementById(id);
            const data = response?.data;
            return data;
        } catch (error) {
            console.log(error);
        }
    }

    const fetchSettlementMetrics = async (brokerId?: string, fromDate?: string, toDate?: string, companyId?: string, isApproved?: boolean, isPaid?: boolean) => {

        try {
            const response: any = await getSettlementStatistics(brokerId, fromDate, toDate, companyId, isApproved, isPaid);

            const data = response?.data;
            return data;
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const handleCompleteSettlement = async (settlement?: Settlement, onSuccess?: () => void) => {
        if (!settlement || settlement === null) return;
        updateModalStates({
            show: true,
            size: 'lg',
            children: (
                <CompleteSettlement
                    onProceed={() => {
                        closeModal();
                        onSuccess?.();
                    }}
                    onClose={closeModal}
                    settlement={settlement}
                />
            ),
            showCloseButton: true,
            title: 'Record Settlement As Completed',
            classes: '!p-0 r-[5px]',
        });
    }


   const handlePageChange = (page: number) => {

    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", page.toString());
    setSearchParams(updatedParams);
  };

    const mainColumns = useMemo(
        () => {
            if ((user.isOwner() || user.isSupervisor())) {
                return [
                    {
                        Header: 'M',
                        columns: [
                            {
                                Header: 'Broker',
                                accessor: 'brokerName',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? row.cell.value : '-'}</span>
                                }
                            },
                            {
                                Header: 'Insurance Company',
                                accessor: 'companyName',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? row.cell.value : '-'}</span>
                                }
                            },
                            {
                                Header: 'Reference',
                                accessor: 'reference',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                                }
                            },
                            {
                                Header: 'Currency',
                                accessor: 'currency',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                                }
                            },
                            {
                                Header: 'Amount payable',
                                accessor: 'netToPay',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? formatCurrencyAmount(row.cell.value) : '-'}</span>;
                                }
                            },
                            {
                                Header: 'Amount paid',
                                accessor: 'amountReceived',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? formatCurrencyAmount(row.cell.value) : '-'}</span>;
                                }
                            },
                            {
                                Header: 'Date',
                                accessor: 'createdOn',
                                Cell: (row: any) => {
                                    return (
                                        <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
                                    );
                                }
                            },

                            {
                                Header: 'Reviewed',
                                accessor: 'isReviewed',
                                Cell: (row: any) => {
                                    return <IconStatus status={row.cell.value} classes="" />;
                                }
                            },
                            {
                                Header: 'Approved',
                                accessor: 'isApproved',
                                Cell: (row: any) => {
                                    return <IconStatus status={row.cell.value} />;
                                }
                            },
                            {
                                Header: 'Paid',
                                accessor: 'isPaid',
                                Cell: (row: any) => {
                                    return <IconStatus status={row.cell.value} />;
                                }
                            },
                            ...(getTableRowActions && getTableRowActions?.length > 0 ? [
                                {
                                    Header: "Action",
                                    id: 'Action',
                                    Cell: (row: any) => {
                                        return (
                                            <GTableActionColumn actions={getTableRowActions?.(row.row.original)} />
                                        );
                                    }
                                }] : [])
                        ]
                    }
                ]
            }

            if (user.isBroker()) {
                return [
                    {
                        Header: 'M',
                        columns: [
                            {
                                Header: 'Agency company',
                                accessor: 'company.name',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? row.cell.value : '-'}</span>
                                }
                            },
                            {
                                Header: 'Reference',
                                accessor: 'reference',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                                }
                            },
                            {
                                Header: 'Currency',
                                accessor: 'currency',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                                }
                            },
                            {
                                Header: 'Amount',
                                accessor: 'grossToPay',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? formatCurrencyAmount(row.cell.value) : '-'}</span>;
                                }
                            },
                            {
                                Header: 'Date',
                                accessor: 'createdOn',
                                Cell: (row: any) => {
                                    return (
                                        <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
                                    );
                                }
                            },

                            {
                                Header: 'Reviewed',
                                accessor: 'isReviewed',
                                Cell: (row: any) => {
                                    return <IconStatus status={row.cell.value} classes="" />;
                                }
                            },
                            {
                                Header: 'Approved',
                                accessor: 'isApproved',
                                Cell: (row: any) => {
                                    return <IconStatus status={row.cell.value} />;
                                }
                            },
                            {
                                Header: 'Paid',
                                accessor: 'isPaid',
                                Cell: (row: any) => {
                                    return <IconStatus status={row.cell.value} />;
                                }
                            },
                            {
                                Header: "Action",
                                id: 'Action',
                                Cell: (row: any) => {
                                    return (
                                        <div className="cursor-pointer flex justify-center">
                                            {getTableRowActions && getTableRowActions(row.row.original).length > 0 && (
                                                <PopOnMe actions={getTableRowActions(row.row.original)}>
                                                    <EllipsisVerticalIcon className="h-4 w-4" />
                                                </PopOnMe>
                                            )}
                                        </div>
                                    );
                                }
                            }
                        ]
                    }
                ]
            }

            return [
                {
                    Header: 'M',
                    columns: [
                        {
                            Header: 'Reference',
                            accessor: 'reference',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Currency',
                            accessor: 'currency',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Amount',
                            accessor: 'grossToPay',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? formatCurrencyAmount(row.cell.value) : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Date',
                            accessor: 'createdOn',
                            Cell: (row: any) => {
                                return (
                                    <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
                                );
                            }
                        },

                        {
                            Header: 'Reviewed',
                            accessor: 'isReviewed',
                            Cell: (row: any) => {
                                return <IconStatus status={row.cell.value} classes="" />;
                            }
                        },
                        {
                            Header: 'Approved',
                            accessor: 'isApproved',
                            Cell: (row: any) => {
                                return <IconStatus status={row.cell.value} />;
                            }
                        },
                        {
                            Header: 'Paid',
                            accessor: 'isPaid',
                            Cell: (row: any) => {
                                return <IconStatus status={row.cell.value} />;
                            }
                        },
                        {
                            Header: "Action",
                            id: 'Action',
                            Cell: (row: any) => {
                                return (
                                    <GTableActionColumn actions={getTableRowActions?.(row.row.original) ?? []} />
                                );
                            }
                        }
                    ]
                }
            ]
        },
        []
    );

    return {
        fetchAllsettlements,
        fetchSettlementById,
        SettlementModal,
        handleCompleteSettlement,
        settlementsPaginationData,
        loading,
        handlePageChange,
        fetchSettlementMetrics,
        mainColumns,
        currentPage,
    }
}

export default useSettlements;


interface ISettlementHookProps {
    getTableRowActions?: (row: any) => any[];
}